import { useState, useCallback, useEffect, useRef } from "react";
import { Upload, FileText, Check, AlertTriangle, Trash2, Link, Loader2, X, Edit2, Package, Plus, ChevronDown, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useClaudeVisionOCR, ChantierSimple } from "@/hooks/useClaudeVisionOCR";
import { useMultiOCR } from "@/hooks/useMultiOCR";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useOCRStore } from "@/stores/ocrStore";
import { QuotaExceededBanner } from "@/components/ui/QuotaExceededBanner";
import MultiOCRResults from "./MultiOCRResults";
import type { ClaudeVisionImportProps, OCRLigne, OCRCommande, ChantierMatch, DoublonDetecte } from "@/types/ocr";
import { cn } from "@/lib/utils";

/**
 * Composant d'import avec extraction OCR Claude Vision
 * - Upload fichier avec preview
 * - Affichage des données extraites (éditables)
 * - Matching multi-chantier
 * - Détection et suppression des doublons
 */
export default function ClaudeVisionImport({
  documentType,
  chantierId,
  statut = 'brouillon',
  onSuccess,
  onCancel,
  onDataExtracted,
  highlightFileUpload = false,
  highlightSaveButton = false
}: ClaudeVisionImportProps) {
  const {
    state,
    isLoading,
    linkedCommandes,
    hasStoredData,
    extractDocument,
    matchChantiers,
    selectChantier,
    editData,
    deleteDuplicateDocument,
    getAllChantiers,
    getChantierReference,
    createChantier,
    saveDocument,
    markCommandesAsLinked,
    reset
  } = useClaudeVisionOCR(chantierId, documentType);

  const { incrementOCR, canUseOCR, entrepriseId } = useSubscription();

  // Hook multi-scan (toujours instancié — inerte tant qu'on n'appelle pas addFiles)
  const multiDocType = documentType === 'ao' ? 'facture' : documentType;
  const multi = useMultiOCR(chantierId || '', multiDocType as 'facture' | 'devis' | 'frais', entrepriseId);
  const ocrStore = useOCRStore();

  // Nom du chantier courant (pour le pré-cochage multi-ref)
  const [chantierNom, setChantierNom] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!chantierId) return;
    supabase
      .from('chantiers')
      .select('nom_chantier')
      .eq('id', chantierId)
      .single()
      .then(({ data }) => { if (data) setChantierNom(data.nom_chantier); });
  }, [chantierId]);

  // État multi-scan
  const [multiMode, setMultiMode] = useState(false);
  // Détection session persistée dans le store
  const hasRestoredSession = !!(
    ocrStore.multiScanSession &&
    ocrStore.multiScanSession.chantierId === (chantierId || '') &&
    ocrStore.multiScanSession.documentType === multiDocType
  );

  // Vérifier si le quota OCR est atteint
  const isQuotaOCRAtteint = !canUseOCR();

  const [dragOver, setDragOver] = useState(false);
  // Commandes sélectionnées (indices des commandes à enregistrer)
  const [selectedCommandes, setSelectedCommandes] = useState<Set<number>>(new Set());

  // Dialogs pour lier aux chantiers
  const [showSelectChantierDialog, setShowSelectChantierDialog] = useState(false);
  const [showCreateChantierDialog, setShowCreateChantierDialog] = useState(false);
  const [chantiersList, setChantiersList] = useState<ChantierSimple[]>([]);
  const [selectedChantierId, setSelectedChantierId] = useState<string>("");
  const [newChantierNom, setNewChantierNom] = useState("");
  const [newChantierClient, setNewChantierClient] = useState("");
  const [newChantierReference, setNewChantierReference] = useState("");
  const [newChantierStatut, setNewChantierStatut] = useState("en_cours");
  const [isCreatingChantier, setIsCreatingChantier] = useState(false);

  // Notifier le parent quand des données sont extraites (pour empêcher fermeture auto)
  // Cela inclut les données restaurées depuis le store après changement de page
  useEffect(() => {
    if (state.step === 'review' && state.extractedData && onDataExtracted) {
      onDataExtracted();
    }
  }, [state.step, state.extractedData, onDataExtracted]);

  // Notifier le parent en mode multi quand des résultats existent
  useEffect(() => {
    if (multiMode && multi.completedCount > 0 && onDataExtracted) {
      onDataExtracted();
    }
  }, [multiMode, multi.completedCount, onDataExtracted]);

  // Log pour debug de la restauration
  useEffect(() => {
    if (hasStoredData) {
      if (import.meta.env.DEV) console.log('[ClaudeVisionImport] Données OCR restaurées depuis le store');
    }
  }, [hasStoredData]);

  // Pré-sélectionner automatiquement les commandes qui matchent la référence du chantier actuel
  // Flag pour éviter de pré-sélectionner plusieurs fois (si l'utilisateur modifie manuellement)
  const hasPreSelectedRef = useRef(false);

  // Réinitialiser le flag quand on revient à l'étape upload (nouveau document)
  useEffect(() => {
    if (state.step === 'upload' || state.step === 'extraction') {
      hasPreSelectedRef.current = false;
    }
  }, [state.step]);

  useEffect(() => {
    const preSelectMatchingCommandes = async () => {
      // Conditions : étape review + plusieurs commandes + chantierId défini + pas encore pré-sélectionné
      if (
        state.step !== 'review' ||
        !state.extractedData?.commandes ||
        state.extractedData.commandes.length <= 1 ||
        !chantierId ||
        hasPreSelectedRef.current
      ) {
        return;
      }

      // Récupérer la référence du chantier actuel
      const chantierRef = await getChantierReference(chantierId);
      if (!chantierRef) return;

      // Chercher les commandes qui matchent (insensible à la casse)
      const refLower = chantierRef.toLowerCase();
      const matchingIndices: number[] = [];

      state.extractedData.commandes.forEach((cmd, idx) => {
        if (cmd.reference) {
          const cmdRefLower = cmd.reference.toLowerCase();
          // Match si la référence contient ou est contenue
          if (cmdRefLower.includes(refLower) || refLower.includes(cmdRefLower)) {
            matchingIndices.push(idx);
          }
        }
      });

      // Pré-sélectionner si des matches trouvés
      if (matchingIndices.length > 0) {
        if (import.meta.env.DEV) console.log('[ClaudeVisionImport] Pré-sélection automatique des commandes:', matchingIndices, 'pour référence:', chantierRef);
        setSelectedCommandes(new Set(matchingIndices));
        hasPreSelectedRef.current = true;
      }
    };

    preSelectMatchingCommandes();
  }, [state.step, state.extractedData, chantierId, getChantierReference]);

  // Toggle sélection d'une commande
  const toggleCommande = useCallback((idx: number) => {
    setSelectedCommandes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  }, []);

  // Calculer le total des commandes sélectionnées
  const getSelectedTotal = useCallback(() => {
    if (!state.extractedData?.commandes) return { ht: 0, ttc: 0 };
    let ht = 0, ttc = 0;
    selectedCommandes.forEach(idx => {
      const cmd = state.extractedData!.commandes[idx];
      if (cmd) {
        ht += cmd.total_ht || 0;
        ttc += cmd.total_ttc || 0;
      }
    });
    return { ht, ttc };
  }, [selectedCommandes, state.extractedData]);

  // Ouvrir le dialog de sélection de chantier existant
  const openSelectChantierDialog = useCallback(async () => {
    const chantiers = await getAllChantiers();
    setChantiersList(chantiers);
    setSelectedChantierId("");
    setShowSelectChantierDialog(true);
  }, [getAllChantiers]);

  // Lier au chantier sélectionné et sauvegarder
  const handleLinkToExistingChantier = async () => {
    if (!selectedChantierId) return;
    setShowSelectChantierDialog(false);

    const docType = documentType === 'ao' ? 'devis' : documentType;
    const commandesIndices = selectedCommandes.size > 0 ? Array.from(selectedCommandes) : undefined;

    // Déterminer si on doit rester ouvert (multi-commandes avec des commandes non liées restantes)
    const totalCommandes = state.extractedData?.commandes?.length || 0;
    const isMultiCommandes = totalCommandes > 1 && commandesIndices && commandesIndices.length > 0;
    const newLinkedCount = linkedCommandes.size + (commandesIndices?.length || 0);
    const keepOpen = isMultiCommandes && newLinkedCount < totalCommandes;

    const id = await saveDocument(docType as 'facture' | 'devis', selectedChantierId, commandesIndices, keepOpen, statut);

    if (id) {
      // Si multi-commandes, marquer comme liées
      if (isMultiCommandes) {
        markCommandesAsLinked(commandesIndices);
        setSelectedCommandes(new Set()); // Reset la sélection pour la prochaine liaison

        // Vérifier si toutes les commandes sont maintenant liées
        if (newLinkedCount >= totalCommandes && onSuccess && state.extractedData) {
          // Toutes liées → fermer
          onSuccess(state.extractedData, [selectedChantierId]);
        }
        // Sinon, le modal reste ouvert
      } else {
        // Pas de multi-commandes ou aucune sélection → comportement normal
        if (onSuccess && state.extractedData) {
          onSuccess(state.extractedData, [selectedChantierId]);
        }
      }
    }
  };

  // Ouvrir le dialog de création de chantier
  const openCreateChantierDialog = () => {
    // Nom vide - l'utilisateur doit le saisir manuellement
    setNewChantierNom("");

    // Référence = première commande cochée OU première référence détectée
    const selectedIdx = Array.from(selectedCommandes)[0];
    const selectedCmd = selectedIdx !== undefined
      ? state.extractedData?.commandes?.[selectedIdx]
      : null;
    const autoRef = selectedCmd?.reference
      || state.extractedData?.references_chantier?.[0]?.reference
      || "";
    setNewChantierReference(autoRef);

    // Statut par défaut
    setNewChantierStatut("en_cours");

    setNewChantierClient("");
    setShowCreateChantierDialog(true);
  };

  // Créer un nouveau chantier et y lier le document
  const handleCreateChantierAndLink = async () => {
    if (!newChantierNom.trim()) return;

    setIsCreatingChantier(true);
    try {
      const newChantierId = await createChantier(
        newChantierNom.trim(),
        newChantierClient.trim() || undefined,
        newChantierReference.trim() || undefined,
        newChantierStatut
      );
      if (newChantierId) {
        setShowCreateChantierDialog(false);

        // Sauvegarder le document avec le nouveau chantier
        const docType = documentType === 'ao' ? 'devis' : documentType;
        const commandesIndices = selectedCommandes.size > 0 ? Array.from(selectedCommandes) : undefined;

        // Déterminer si on doit rester ouvert (multi-commandes avec des commandes non liées restantes)
        const totalCommandes = state.extractedData?.commandes?.length || 0;
        const isMultiCommandes = totalCommandes > 1 && commandesIndices && commandesIndices.length > 0;
        const newLinkedCount = linkedCommandes.size + (commandesIndices?.length || 0);
        const keepOpen = isMultiCommandes && newLinkedCount < totalCommandes;

        const id = await saveDocument(docType as 'facture' | 'devis', newChantierId, commandesIndices, keepOpen, statut);

        if (id) {
          // Si multi-commandes, marquer comme liées
          if (isMultiCommandes) {
            markCommandesAsLinked(commandesIndices);
            setSelectedCommandes(new Set()); // Reset la sélection pour la prochaine liaison

            // Vérifier si toutes les commandes sont maintenant liées
            if (newLinkedCount >= totalCommandes && onSuccess && state.extractedData) {
              // Toutes liées → fermer
              onSuccess(state.extractedData, [newChantierId]);
            }
            // Sinon, le modal reste ouvert
          } else {
            // Pas de multi-commandes ou aucune sélection → comportement normal
            if (onSuccess && state.extractedData) {
              onSuccess(state.extractedData, [newChantierId]);
            }
          }
        }
      }
    } finally {
      setIsCreatingChantier(false);
    }
  };

  // Gérer le drop de fichier
  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    // Mode multi : >1 fichier → addFiles() fait la validation stricte via validateFile
    if (droppedFiles.length > 1) {
      await multi.addFiles(Array.from(droppedFiles));
      setMultiMode(true);
      multi.startProcessing();
      return;
    }

    // Mode mono (identique à l'actuel)
    const file = droppedFiles[0];
    if (file && (file.type.includes('pdf') || file.type.includes('image'))) {
      const result = await extractDocument(file, documentType);
      if (result?.success) {
        await incrementOCR();
      }
    }
  }, [extractDocument, documentType, incrementOCR, multi]);

  // Gérer la sélection de fichier
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    // Mode multi : >1 fichier
    if (selectedFiles.length > 1) {
      await multi.addFiles(Array.from(selectedFiles));
      setMultiMode(true);
      multi.startProcessing();
      return;
    }

    // Mode mono (identique à l'actuel)
    const file = selectedFiles[0];
    if (file) {
      const result = await extractDocument(file, documentType);
      if (result?.success) {
        await incrementOCR();
      }
    }
  }, [extractDocument, documentType, incrementOCR, multi]);

  // Sauvegarder et fermer
  // Passe le chantierId du composant parent à saveDocument
  // Si multi-commandes, passe les indices sélectionnés
  const handleSave = async () => {
    const docType = documentType === 'ao' ? 'devis' : documentType;
    // Convertir le Set en Array pour le passer au hook
    const commandesIndices = selectedCommandes.size > 0 ? Array.from(selectedCommandes) : undefined;
    const id = await saveDocument(docType as 'facture' | 'devis', chantierId, commandesIndices, undefined, statut);
    if (id && onSuccess && state.extractedData) {
      const chantiersLies = Object.values(state.selectedChantiers);
      onSuccess(state.extractedData, chantiersLies);
    }
  };

  // Formater un montant en euros
  const formatMontant = (value: number | null | undefined): string => {
    if (value == null) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  // === Mode multi-scan ===
  if (multiMode) {
    return (
      <MultiOCRResults
        files={multi.files}
        allResultLines={multi.allResultLines}
        isProcessing={multi.isProcessing}
        currentIndex={multi.currentIndex}
        completedCount={multi.completedCount}
        totalCount={multi.totalCount}
        savedCount={multi.savedCount}
        pendingSaveCount={multi.pendingSaveCount}
        chantierId={chantierId || ''}
        chantierNom={chantierNom}
        documentType={multiDocType as 'facture' | 'devis' | 'frais'}
        restoredFromStore={multi.restoredFromStore}
        onLinkToCurrentChantier={multi.linkToCurrentChantier}
        onLinkToOtherChantier={multi.linkToOtherChantier}
        onCreateAndLink={multi.createAndLink}
        onLinkAllToCurrentChantier={multi.linkAllToCurrentChantier}
        onRetry={multi.retryExtraction}
        onDismiss={multi.dismissLine}
        onCancel={multi.cancelProcessing}
        onClose={() => {
          setMultiMode(false);
          onCancel?.();
        }}
        entrepriseId={entrepriseId}
      />
    );
  }

  // Étape 1: Upload
  if (state.step === 'upload') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {documentType === 'facture' ? 'Importer une facture' : documentType === 'devis' ? 'Importer un devis' : documentType === 'frais' ? 'Importer une dépense' : 'Importer un appel d\'offres'}
          </CardTitle>
          <CardDescription>
            Glissez un ou plusieurs fichiers PDF ou images, ou cliquez pour sélectionner
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Bandeau de restauration session multi-scan persistée */}
          {hasRestoredSession && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-3">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                Vous avez des résultats de scan en attente.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => {
                    const restored = multi.restoreFromStore();
                    if (restored) setMultiMode(true);
                  }}
                >
                  Reprendre
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => ocrStore.clearMultiScanSession()}
                >
                  Nouveau scan
                </Button>
              </div>
            </div>
          )}

          {/* Afficher le banner de quota atteint si nécessaire */}
          {isQuotaOCRAtteint ? (
            <div className="py-6">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
              <QuotaExceededBanner type="ocr" variant="banner" className="mt-4" />
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                PDF, JPG ou PNG (max 20 Mo)
              </p>
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleFileSelect}
              />
              <Button asChild variant="outline" className={cn(highlightFileUpload && "animate-highlight-pulse")}>
                <label htmlFor="file-upload" className="cursor-pointer">
                  Choisir un ou plusieurs fichiers
                </label>
              </Button>
            </div>
          )}

          {onCancel && (
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={onCancel}>
                Annuler
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Étape 2: Extraction en cours
  if (state.step === 'extraction') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium">Extraction en cours...</p>
            <p className="text-sm text-muted-foreground">
              Analyse du document avec B8ild AI Vision
            </p>
            <Progress value={33} className="w-64" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Étape 3: Review des données extraites
  if (state.step === 'review' && state.extractedData) {
    const data = state.extractedData;
    const confidence = state.confidence;

    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Données extraites</CardTitle>
              <CardDescription>
                Vérifiez et corrigez si nécessaire
              </CardDescription>
            </div>
            <Badge variant={confidence >= 0.8 ? "default" : confidence >= 0.6 ? "secondary" : "destructive"}>
              Confiance: {Math.round(confidence * 100)}%
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Informations générales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Type</Label>
              <p className="font-medium capitalize">{data.type_document}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Numéro</Label>
              <Input
                value={state.editedData.numero_document ?? data.numero_document ?? ''}
                onChange={(e) => editData('numero_document', e.target.value)}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input
                type="date"
                value={state.editedData.date_document ?? data.date_document ?? ''}
                onChange={(e) => editData('date_document', e.target.value)}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Objet</Label>
              <p className="font-medium truncate">{data.objet_travaux || '-'}</p>
            </div>
          </div>

          <Separator />

          {/* Émetteur */}
          {data.emetteur && (
            <div>
              <h4 className="font-medium mb-2">Fournisseur / Émetteur</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Nom</Label>
                  <p>{data.emetteur.nom || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">SIRET</Label>
                  <p className="font-mono">{data.emetteur.siret || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Ville</Label>
                  <p>{data.emetteur.ville || '-'}</p>
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Totaux */}
          <div>
            <h4 className="font-medium mb-2">Montants</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <Label className="text-xs text-muted-foreground">Total HT</Label>
                <p className="text-lg font-bold">{formatMontant(data.totaux?.total_ht)}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <Label className="text-xs text-muted-foreground">TVA ({data.totaux?.taux_tva || 0}%)</Label>
                <p className="text-lg font-bold">{formatMontant(data.totaux?.montant_tva)}</p>
              </div>
              <div className="bg-primary/10 rounded-lg p-3 text-center">
                <Label className="text-xs text-muted-foreground">Total TTC</Label>
                <p className="text-xl font-bold text-primary">{formatMontant(data.totaux?.total_ttc)}</p>
              </div>
            </div>
          </div>

          {/* Sélection des commandes (multi-référence) - AFFICHÉ EN PREMIER */}
          {data.commandes && data.commandes.length > 1 && (
            <>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    {data.commandes.length} commandes détectées
                    {linkedCommandes.size > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {linkedCommandes.size} liée(s)
                      </Badge>
                    )}
                  </h4>
                  <Badge variant="secondary">
                    Sélectionné: {formatMontant(getSelectedTotal().ht)}
                  </Badge>
                </div>
                <Alert className="mb-3">
                  <AlertDescription className="text-sm">
                    {linkedCommandes.size > 0
                      ? `${linkedCommandes.size}/${data.commandes.length} commandes liées. Continuez avec les commandes restantes.`
                      : 'Cette facture contient plusieurs commandes avec des références différentes. Cochez celles à associer au chantier actuel.'}
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  {data.commandes.map((cmd: OCRCommande, idx: number) => {
                    const isLinked = linkedCommandes.has(idx);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                          isLinked
                            ? 'border-green-500/50 bg-green-500/10 opacity-60 cursor-not-allowed'
                            : selectedCommandes.has(idx)
                            ? 'border-primary bg-primary/5 cursor-pointer'
                            : 'border-muted hover:border-muted-foreground/50 cursor-pointer'
                        }`}
                        onClick={() => !isLinked && toggleCommande(idx)}
                      >
                        <div className="flex items-center gap-3">
                          {isLinked ? (
                            <Check className="h-5 w-5 text-green-500" />
                          ) : (
                            <Checkbox
                              checked={selectedCommandes.has(idx)}
                              onCheckedChange={() => toggleCommande(idx)}
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant={isLinked ? "secondary" : selectedCommandes.has(idx) ? "default" : "outline"}>
                                {cmd.reference || 'Sans référence'}
                              </Badge>
                              {isLinked && (
                                <Badge variant="outline" className="text-green-600 border-green-500">
                                  Liée ✓
                                </Badge>
                              )}
                              {cmd.numero_commande && (
                                <span className="text-xs text-muted-foreground">
                                  Cmd: {cmd.numero_commande}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {cmd.nb_lignes} ligne(s)
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-medium ${isLinked ? 'text-muted-foreground' : ''}`}>
                            {formatMontant(cmd.total_ht)}
                          </p>
                          {cmd.total_ttc != null && cmd.total_ttc > 0 && (
                            <p className="text-xs text-muted-foreground">
                              TTC: {formatMontant(cmd.total_ttc)}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Références chantier détectées */}
          {data.references_chantier && data.references_chantier.length > 0 && (
            <Alert>
              <Link className="h-4 w-4" />
              <AlertDescription>
                <strong>{data.references_chantier.length} référence(s) chantier détectée(s) :</strong>{' '}
                {data.references_chantier.map(r => r.reference).join(', ')}
              </AlertDescription>
            </Alert>
          )}

          {/* Boutons d'action - Placés juste après les références */}
          <div className="flex justify-between items-center gap-2 py-2">
            {/* Bouton Annuler - à gauche */}
            <Button variant="outline" onClick={reset}>
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>

            <div className="flex gap-2">
              {/* Bouton Terminer si toutes les commandes sont liées */}
              {data.commandes && data.commandes.length > 1 && linkedCommandes.size === data.commandes.length && (
                <Button
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    if (onSuccess && state.extractedData) {
                      onSuccess(state.extractedData, []);
                    }
                  }}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Terminer ({linkedCommandes.size} liées)
                </Button>
              )}

              {/* Menu déroulant pour lier aux chantiers - masqué si toutes les commandes sont liées */}
              {!(data.commandes && data.commandes.length > 1 && linkedCommandes.size === data.commandes.length) && (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary">
                        <Link className="h-4 w-4 mr-2" />
                        Lier autre chantier
                        <ChevronDown className="h-4 w-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={openSelectChantierDialog}>
                        <Building2 className="h-4 w-4 mr-2" />
                        Lier à un chantier existant
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={openCreateChantierDialog}>
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter à nouveau chantier
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button onClick={handleSave} disabled={isLoading} className={cn(highlightSaveButton && "animate-highlight-pulse")}>
                    {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    Enregistrer
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Lignes (aperçu) - AFFICHÉ APRÈS LES COMMANDES */}
          {data.lignes && data.lignes.length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="font-medium mb-2">Lignes ({data.lignes.length})</h4>
                <ScrollArea className="h-48 rounded border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Désignation</TableHead>
                        <TableHead className="text-right">Qté</TableHead>
                        <TableHead className="text-right">PU HT</TableHead>
                        <TableHead className="text-right">Total HT</TableHead>
                        <TableHead>Chantier</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.lignes.map((ligne: OCRLigne, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs">{ligne.numero_ligne}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{ligne.designation}</TableCell>
                          <TableCell className="text-right">{ligne.quantite} {ligne.unite}</TableCell>
                          <TableCell className="text-right">{formatMontant(ligne.prix_unitaire_ht)}</TableCell>
                          <TableCell className="text-right font-medium">{formatMontant(ligne.total_ht)}</TableCell>
                          <TableCell>
                            {ligne.reference_chantier && (
                              <Badge variant="outline" className="text-xs">
                                {ligne.reference_chantier}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            </>
          )}


          {/* Dialog: Sélectionner un chantier existant */}
          <Dialog open={showSelectChantierDialog} onOpenChange={setShowSelectChantierDialog}>
            <DialogContent variant="industrial">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Lier à un chantier existant
                </DialogTitle>
                <DialogDescription>
                  Sélectionnez le chantier auquel associer {selectedCommandes.size > 0 ? 'les commandes sélectionnées' : 'ce document'}.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label className="mb-2 block">Chantier</Label>
                <Select value={selectedChantierId} onValueChange={setSelectedChantierId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un chantier..." />
                  </SelectTrigger>
                  <SelectContent>
                    <ScrollArea className="h-60">
                      {chantiersList.map((chantier) => (
                        <SelectItem key={chantier.id} value={chantier.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{chantier.nom}</span>
                            {chantier.client && (
                              <span className="text-xs text-muted-foreground">{chantier.client}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSelectChantierDialog(false)} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
                  Annuler
                </Button>
                <Button onClick={handleLinkToExistingChantier} disabled={!selectedChantierId || isLoading} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
                  {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  Lier et enregistrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Dialog: Créer un nouveau chantier */}
          <Dialog open={showCreateChantierDialog} onOpenChange={setShowCreateChantierDialog}>
            <DialogContent variant="industrial">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Créer un nouveau chantier
                </DialogTitle>
                <DialogDescription>
                  Créez un nouveau chantier et associez-y {selectedCommandes.size > 0 ? 'les commandes sélectionnées' : 'ce document'}.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                {/* Nom du chantier - OBLIGATOIRE */}
                <div>
                  <Label htmlFor="new-chantier-nom" className="mb-2 block">Nom du chantier *</Label>
                  <Input
                    id="new-chantier-nom"
                    value={newChantierNom}
                    onChange={(e) => setNewChantierNom(e.target.value)}
                    placeholder="Ex: Villa DUPONT, Rénovation Appartement..."
                  />
                </div>

                {/* Référence chantier - AUTO-REMPLI */}
                <div>
                  <Label htmlFor="new-chantier-ref" className="mb-2 block">Référence chantier</Label>
                  <Input
                    id="new-chantier-ref"
                    value={newChantierReference}
                    onChange={(e) => setNewChantierReference(e.target.value)}
                    placeholder="Ex: PISCINE, REF-2025-001..."
                  />
                </div>

                {/* Statut - SELECT */}
                <div>
                  <Label htmlFor="new-chantier-statut" className="mb-2 block">Statut *</Label>
                  <Select value={newChantierStatut} onValueChange={setNewChantierStatut}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="brouillon">Brouillon</SelectItem>
                      <SelectItem value="projection">Projection</SelectItem>
                      <SelectItem value="attente_signature">En attente de signature</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="suspendu">Suspendu</SelectItem>
                      <SelectItem value="termine">Termine</SelectItem>
                      <SelectItem value="annule">Annule</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Nom du client - OPTIONNEL */}
                <div>
                  <Label htmlFor="new-chantier-client" className="mb-2 block">Nom du client (optionnel)</Label>
                  <Input
                    id="new-chantier-client"
                    value={newChantierClient}
                    onChange={(e) => setNewChantierClient(e.target.value)}
                    placeholder="Ex: M. Dupont, SCI Les Oliviers..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateChantierDialog(false)} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
                  Annuler
                </Button>
                <Button onClick={handleCreateChantierAndLink} disabled={!newChantierNom.trim() || isCreatingChantier} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
                  {isCreatingChantier ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Créer et lier
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  // Étape 4: Matching avec les chantiers
  if (state.step === 'matching' && state.matchingResult) {
    const { matches, doublons_detectes } = state.matchingResult;

    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Lier aux chantiers
          </CardTitle>
          <CardDescription>
            Associez chaque référence détectée à un chantier existant
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Doublons détectés */}
          {doublons_detectes.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Attention :</strong> {doublons_detectes.length} doublon(s) détecté(s)
              </AlertDescription>
            </Alert>
          )}

          {doublons_detectes.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-destructive">Documents similaires existants</h4>
              {doublons_detectes.map((doublon: DoublonDetecte) => (
                <div
                  key={doublon.document_id}
                  className="flex items-center justify-between p-3 border border-destructive/50 rounded-lg bg-destructive/5"
                >
                  <div className="text-sm">
                    <span className="font-medium">{doublon.numero_document}</span>
                    <span className="mx-2 text-muted-foreground">|</span>
                    <span>{doublon.date_document}</span>
                    <span className="mx-2 text-muted-foreground">|</span>
                    <span>{formatMontant(doublon.montant_ttc)}</span>
                    <span className="mx-2 text-muted-foreground">|</span>
                    <span className="text-muted-foreground">{doublon.chantier_nom}</span>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteDuplicateDocument(doublon.document_id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
              ))}
            </div>
          )}

          <Separator />

          {/* Matching chantiers */}
          {matches.length > 0 ? (
            <div className="space-y-4">
              <h4 className="font-medium">Correspondances trouvées</h4>
              {matches.map((match: ChantierMatch, idx: number) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 border rounded-lg ${
                    match.deja_lie ? 'border-amber-500/50 bg-amber-50 dark:bg-amber-950/20' : 'border-muted'
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{match.reference_detectee}</Badge>
                      <span className="text-muted-foreground">→</span>
                      <span className="font-medium">{match.chantier_nom}</span>
                      <Badge variant="secondary" className="text-xs">
                        {Math.round(match.score_matching * 100)}%
                      </Badge>
                    </div>
                    {match.deja_lie && (
                      <p className="text-xs text-amber-600 mt-1">
                        Ce document est déjà lié à ce chantier
                      </p>
                    )}
                  </div>
                  <Button
                    variant={state.selectedChantiers[match.reference_detectee] === match.chantier_id ? "default" : "outline"}
                    size="sm"
                    onClick={() => selectChantier(match.reference_detectee, match.chantier_id)}
                    disabled={match.deja_lie}
                  >
                    {state.selectedChantiers[match.reference_detectee] === match.chantier_id ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Sélectionné
                      </>
                    ) : (
                      'Sélectionner'
                    )}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                Aucune correspondance automatique trouvée. Le document sera enregistré sans liaison.
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => reset()}>
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={isLoading} className={cn(highlightSaveButton && "animate-highlight-pulse")}>
              {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Étape 5: Sauvegarde en cours
  if (state.step === 'saving') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium">Enregistrement...</p>
            <Progress value={66} className="w-64" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback
  return null;
}
